import { Injectable } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';
import { NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { interval, Subscription } from 'rxjs';
import { ModalConfirmationComponent } from '../../components/modal-confirmation/modal-confirmation.component';
import { filter, first, tap } from 'rxjs/operators';
import { ExplainModuleEnum } from 'src/app/core/user/user.model';
import { ActivationStart, Router } from '@angular/router';

import VERSION_CONFIG from 'src/assets/mep-configuration/version.json';

@Injectable({
  providedIn: 'root',
})
export class UpdateModalService {
  currentModule?: ExplainModuleEnum;
  checkForUpdateIntervalSubscription!: Subscription;
  updateModal!: NgbModalRef;
  updateModalNoSnooze!: NgbModalRef;
  ngbModalOptions: NgbModalOptions = {
    backdrop: 'static',
    keyboard: false,
    ariaLabelledBy: 'modal-basic-title',
    centered: true,
    windowClass: 'custom-modal-update',
  };
  // Time for snoozing, in milliseconds
  snoozeTime = 120000;

  constructor(
    private update: SwUpdate,
    private modalService: NgbModal,
    private router: Router
  ) {
    this.router.events
      .pipe(filter(event => event instanceof ActivationStart))
      .pipe(first())
      .subscribe((event: any) => {
        this.currentModule = event.snapshot.data.module;
      });
  }

  /**
   * Set up change detection every x minutes. If change is detected by the service worker, display a first modal with snooze allowed.
   * Timer is unregistered in that case.
   * When snooze button hit, timer of updateModalService.snoozeTime is triggered to display second modal without snooze button.
   * Action performed with modal is a window.reload(true).
   * @param milliseconds - time for interval in milliseconds
   */
  public setUpdateDetectionTimer(milliseconds: number) {
    const checkForUpdateInterval = interval(milliseconds);

    // Subscribing to interval for checking updates every x milliseconds
    this.checkForUpdateIntervalSubscription = checkForUpdateInterval
      .pipe(
        tap(() => {
          this.update.checkForUpdate().then();
        })
      )
      .subscribe();

    // subscribe to detection of application update
    this.update.versionUpdates
      .pipe(
        tap(versionEvent => {
          if (versionEvent.type === 'VERSION_DETECTED') {
            const moduleCondition = VERSION_CONFIG.target_update_modal_module.length
              ? (VERSION_CONFIG.target_update_modal_module as ExplainModuleEnum) === this.currentModule
              : true;
            this.openUpdateModal(VERSION_CONFIG.display_update_modal && moduleCondition);
          }
        })
      )
      .subscribe();
  }

  /**
   * Method to open an update modal in case of true boolean as param.
   * @param displayBoolean - boolean set in version.json to tell the app to display modal after a new deployment
   */
  openUpdateModal(displayBoolean: boolean) {
    if (displayBoolean) {
      // Gestion of the modale type 'generic' (legacy) or 'new-feature'
      const modalOptions = this.getModalOptions();

      // If there is no opened modal
      if (!this.modalService.hasOpenModals()) {
        // Open generic update modal or specific new feature description modal
        this.updateModal = this.modalService.open(ModalConfirmationComponent, this.ngbModalOptions);
        this.updateModal.componentInstance.updateMode = modalOptions.updateMode;
        this.updateModal.componentInstance.snooze = modalOptions.snooze;
        this.updateModal.componentInstance.returnUrl = modalOptions.returnUrl;
        this.updateModal.componentInstance.title = modalOptions.title;
        this.updateModal.componentInstance.content = modalOptions.content;
        this.updateModal.componentInstance.explanation = modalOptions.explanation;
        this.updateModal.componentInstance.explanationUnderline = modalOptions.explanationUnderline;
        this.updateModal.componentInstance.btnName = modalOptions.btnName;
        this.updateModal.componentInstance.snoozeFunction = modalOptions.snoozeFunction;

        // Unsubscribing to avoid issues when displaying 2nd modal.
        if (this.checkForUpdateIntervalSubscription) {
          this.checkForUpdateIntervalSubscription.unsubscribe();
        }
      }
    }
  }

  getModalOptions(): ModalOptions {
    return {
      updateMode: true,
      snooze: true,
      returnUrl: true,
      title: 'application-update.modal-title',
      content: 'application-update.modal-body1',
      explanation: 'application-update.modal-explanation',
      explanationUnderline: 'application-update.modal-explanation-underline',
      btnName: 'application-update.modal-yes',
      snoozeFunction: () => this.onUpdateModalSnooze(),
    };
  }

  /** Display of second modal (without the snooze button) after snoozeTime */
  onUpdateModalSnooze() {
    setTimeout(() => {
      this.updateModalNoSnooze = this.modalService.open(ModalConfirmationComponent, { ...this.ngbModalOptions });
      this.updateModalNoSnooze.componentInstance.updateMode = true;
      this.updateModalNoSnooze.componentInstance.snooze = false;
      this.updateModalNoSnooze.componentInstance.returnUrl = '';
      this.updateModalNoSnooze.componentInstance.title = 'application-update.modal-title';
      this.updateModalNoSnooze.componentInstance.content = 'application-update.modal-body1';
      this.updateModalNoSnooze.componentInstance.btnName = 'application-update.modal-yes';
    }, this.snoozeTime);
  }
}

export interface ModalOptions {
  updateMode: boolean;
  snooze: boolean;
  returnUrl: boolean;
  title: string;
  content: string;
  explanation: string;
  explanationUnderline: string;
  btnName: string;
  snoozeFunction: Function;
}
